import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import "@/App.css";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Riders from "@/pages/Riders";
import RiderDetail from "@/pages/RiderDetail";
import Assessments from "@/pages/Assessments";
import AssessmentDetail from "@/pages/AssessmentDetail";
import AssessmentWizard from "@/pages/AssessmentWizard";
import AssessmentSlots from "@/pages/AssessmentSlots";
import PublicLayout from "@/components/public/PublicLayout";
import PublicHome from "@/pages/public/Home";
import Volunteer from "@/pages/public/Volunteer";
import { PostDetail, PostList } from "@/pages/public/Posts";
import Applications from "@/pages/Applications";
import SiteContent from "@/pages/SiteContent";
import Users from "@/pages/Users";
import Documents from "@/pages/Documents";
import MyStatus from "@/pages/MyStatus";
import Renewals from "@/pages/Renewals";
import Account from "@/pages/Account";
import Fleet from "@/pages/Fleet";
import BikeCheck from "@/pages/BikeCheck";
import RunDetail from "@/pages/RunDetail";
import Appeals from "@/pages/Appeals";
import TrusteeHub from "@/pages/TrusteeHub";
import RideSetup from "@/pages/RideSetup";
import BikeDetail from "@/pages/BikeDetail";
import Permissions from "@/pages/Permissions";
import Rota from "@/pages/Rota";
import Control from "@/pages/Control";
import Availability from "@/pages/Availability";
import Bookings from "@/pages/Bookings";
import Dispatch from "@/pages/Dispatch";
import MyJobs from "@/pages/MyJobs";
import JobDetail from "@/pages/JobDetail";
import Hospitals from "@/pages/Hospitals";
import HospitalPortal from "@/pages/HospitalPortal";
import JobReports from "@/pages/JobReports";
import RequestJob from "@/pages/public/RequestJob";
import Feedback from "@/pages/public/Feedback";
import { homeFor } from "@/lib/constants";

const Loading = () => (
  <div className="flex min-h-screen items-center justify-center">
    <img src="/brand/logo.png" alt="" className="h-16 w-16 animate-pulse" />
  </div>
);

const Protected = ({ roles, f, lvl = 1, children }) => {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname + loc.search }} />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homeFor(user)} replace />;
  if (f && (user.permissions?.[f] || 0) < lvl) return <Navigate to={homeFor(user)} replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route element={<PublicLayout />}>
              <Route path="/" element={<PublicHome />} />
              <Route path="/volunteer" element={<Volunteer />} />
              <Route path="/news" element={<PostList kinds={["news"]} />} />
              <Route path="/events" element={<PostList kinds={["event", "talk"]} />} />
              <Route path="/news/:slug" element={<PostDetail />} />
              <Route path="/request-transport" element={<RequestJob />} />
              <Route path="/feedback/:token" element={<Feedback />} />
            </Route>
            <Route element={<Protected><Layout /></Protected>}>
              <Route path="/dashboard" element={<Protected f="dashboard"><Dashboard /></Protected>} />
              <Route path="/applications" element={<Protected roles={["admin"]}><Applications /></Protected>} />
              <Route path="/site-content" element={<Protected roles={["admin"]}><SiteContent /></Protected>} />
              <Route path="/riders" element={<Protected f="riders"><Riders /></Protected>} />
              <Route path="/renewals" element={<Protected f="riders"><Renewals /></Protected>} />
              <Route path="/riders/:id" element={<Protected f="riders"><RiderDetail /></Protected>} />
              <Route path="/assessments" element={<Protected f="assessments"><Assessments /></Protected>} />
              <Route path="/slots" element={<Protected f="conduct_assessments" lvl={2}><AssessmentSlots /></Protected>} />
              <Route path="/assessments/:id" element={<AssessmentDetail />} />
              <Route path="/assess/new" element={<Protected f="conduct_assessments" lvl={2}><AssessmentWizard /></Protected>} />
              <Route path="/users" element={<Protected roles={["admin"]}><Users /></Protected>} />
              <Route path="/permissions" element={<Protected roles={["admin"]}><Permissions /></Protected>} />
              <Route path="/documents" element={<Protected f="documents"><Documents /></Protected>} />
              <Route path="/my" element={<Protected roles={["rider"]}><MyStatus /></Protected>} />
              <Route path="/account" element={<Account />} />
              <Route path="/fleet" element={<Protected f="fleet"><Fleet /></Protected>} />
              <Route path="/bike-check" element={<Protected f="fleet"><BikeCheck /></Protected>} />
              <Route path="/bike-check/:runId/post" element={<Protected f="fleet"><BikeCheck /></Protected>} />
              <Route path="/runs/:id" element={<RunDetail />} />
              <Route path="/appeals" element={<Protected f="appeals"><Appeals /></Protected>} />
              <Route path="/trustees" element={<Protected f="reports"><TrusteeHub /></Protected>} />
              <Route path="/ride-setup" element={<Protected f="ride_setup" lvl={3}><RideSetup /></Protected>} />
              <Route path="/fleet/bikes/:id" element={<BikeDetail />} />
              <Route path="/rota" element={<Protected f="rota"><Rota /></Protected>} />
              <Route path="/control" element={<Protected f="control"><Control /></Protected>} />
              <Route path="/availability" element={<Protected f="availability"><Availability /></Protected>} />
              <Route path="/bookings" element={<Protected f="bookings"><Bookings /></Protected>} />
              <Route path="/dispatch" element={<Protected f="jobs" lvl={2}><Dispatch /></Protected>} />
              <Route path="/my-jobs" element={<Protected f="jobs"><MyJobs /></Protected>} />
              <Route path="/jobs/:id" element={<JobDetail />} />
              <Route path="/hospitals" element={<Protected f="jobs" lvl={3}><Hospitals /></Protected>} />
              <Route path="/job-reports" element={<Protected f="jobs" lvl={2}><JobReports /></Protected>} />
              <Route path="/hospital" element={<Protected roles={["hospital"]}><HospitalPortal /></Protected>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster theme="dark" position="top-center" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
